import { mapGetters } from 'vuex';
import { isWahaInbox } from 'dashboard/api/wahaManager';
import { mediaUploadApi, mediaUploadError } from 'dashboard/helper/mediaChunkUpload';
import { useAlert } from 'dashboard/composables';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { getMaxUploadSizeByChannel } from '@chatwoot/utils';
import { DirectUpload } from 'activestorage';
import { setDirectUploadAuthHeaders } from 'dashboard/helper/directUploadsHelper';
import {
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export default {
  data() { return { bcwVideoUploads: [], bcwVideoUrls: new Set() }; },
  watch: {
    editorStateId() { this.bcwVideoUploads = []; },
    attachedFiles(next, previous) {
      for (const item of previous || []) {
        if (this.bcwVideoUrls.has(item.thumb) && !next.some(value => value.thumb === item.thumb)) {
          URL.revokeObjectURL(item.thumb); this.bcwVideoUrls.delete(item.thumb);
        }
      }
    },
  },
  beforeUnmount() {
    for (const url of this.bcwVideoUrls) URL.revokeObjectURL(url);
    this.bcwVideoUrls.clear();
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    installationLimit() {
      return resolveMaximumFileUploadSize(
        this.globalConfig.maximumFileUploadSize
      );
    },
  },

  methods: {
    maxSizeFor(mime) {
      // Use default/installation limit for private notes
      if (this.isOnPrivateNote) {
        return this.installationLimit;
      }

      const channelType = this.inbox?.channel_type;

      if (!channelType || channelType === INBOX_TYPES.WEB) {
        return this.installationLimit;
      }

      const channelLimit = getMaxUploadSizeByChannel({
        channelType,
        medium: this.inbox?.medium, // e.g. 'sms' | 'whatsapp'
        mime, // e.g. 'image/png'
      });

      if (channelLimit === DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE) {
        return this.installationLimit;
      }

      return Math.min(channelLimit, this.installationLimit);
    },
    alertOverLimit(maxSizeMB) {
      useAlert(
        this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
        })
      );
    },
    async onFileUpload(file) {
      const actual = file?.file;
      if (actual && !this.isOnPrivateNote && isWahaInbox(this.inbox) &&
          (actual.type.startsWith('video/') || /\.(mp4|mov|m4v|webm|mkv|avi|3gp)$/i.test(actual.name))) {
        const origin = this.captureAttachmentContext?.();
        if (!origin) return;
        try {
          const capabilities = await mediaUploadApi(origin.accountId, origin.conversationId).capabilities();
          if (!this.attachmentContextMatches(origin)) return;
          if (capabilities.enabled && capabilities.outbound_enabled !== false) {
            if (this.bcwVideoUploads.length >= 3) { useAlert('请先完成已选择的视频上传'); return; }
            this.bcwVideoUploads.push({ id: crypto.randomUUID(), file, origin, capabilities });
            return;
          }
        } catch (error) { useAlert(mediaUploadError(error)); return; }
      }
      if (this.globalConfig.directUploadsEnabled) {
        this.onDirectFileUpload(file);
      } else {
        this.onIndirectFileUpload(file);
      }
    },

    onDirectFileUpload(file) {
      if (!file) return;

      const origin = this.captureAttachmentContext?.();
      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      const upload = new DirectUpload(
        file.file,
        `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            setDirectUploadAuthHeaders(xhr);
          },
        }
      );

      upload.create((error, blob) => {
        if (error) {
          useAlert(error);
        } else {
          this.attachFile({ file, blob, origin });
        }
      });
    },

    onIndirectFileUpload(file) {
      if (!file) return;

      const origin = this.captureAttachmentContext?.();
      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      this.attachFile({ file, origin });
    },
  },
};
